import * as ts from 'typescript'

export interface InferredTypeResult {
  type: string
  fields?: Array<{ name: string; type: string; description?: string }>
  error?: string
}

/**
 * Creates a virtual TypeScript source file with variable declarations and code
 */
function createVirtualSourceFile(
  code: string,
  variableTypes: Record<string, string>
): ts.SourceFile {
  // Create variable declarations
  const variableDeclarations = Object.entries(variableTypes)
    .map(([name, type]) => {
      // Handle complex types (arrays, objects, etc.)
      const tsType = convertToTypeScriptType(type)
      return `declare const ${name}: ${tsType};`
    })
    .join('\n')

  // Wrap code in a function to analyze return type
  // If code already has 'return', use it as is, otherwise wrap it
  const hasReturn = /\breturn\b/.test(code.trim())
  const wrappedCode = hasReturn
    ? code.trim()
    : `return (${code.trim()});`

  const fullCode = `
    ${variableDeclarations}
    
    function __inferType() {
      ${wrappedCode}
    }
  `

  return ts.createSourceFile(
    'temp.ts',
    fullCode,
    ts.ScriptTarget.Latest,
    true,
    ts.ScriptKind.TS
  )
}

/**
 * Converts a type string to TypeScript type syntax
 */
function convertToTypeScriptType(type: string): string {
  // Handle common patterns
  if (type === 'any') return 'any'
  if (type === 'number') return 'number'
  if (type === 'string') return 'string'
  if (type === 'boolean') return 'boolean'
  if (type === 'void') return 'void'
  if (type === 'null') return 'null'
  if (type === 'undefined') return 'undefined'

  // Handle object types like "{ latitude: number; longitude: number }"
  if (type.startsWith('{') && type.endsWith('}')) {
    return type
  }

  // Handle array types
  if (type.includes('[]') || type.includes('array')) {
    return 'any[]'
  }

  // Default to any for unknown types
  return 'any'
}

/**
 * Extracts the return type from a function in the source file
 */
function extractReturnType(
  sourceFile: ts.SourceFile,
  typeChecker: ts.TypeChecker
): InferredTypeResult {
  try {
    // Find the function declaration
    let functionNode: ts.FunctionDeclaration | null = null

    function visit(node: ts.Node) {
      if (ts.isFunctionDeclaration(node) && node.name?.text === '__inferType') {
        functionNode = node
        return
      }
      ts.forEachChild(node, visit)
    }

    visit(sourceFile)

    if (!functionNode) {
      return { type: 'unknown', error: 'Could not find function declaration' }
    }

    // Get the return type
    const signature = typeChecker.getSignatureFromDeclaration(functionNode)
    if (!signature) {
      return { type: 'unknown', error: 'Could not get function signature' }
    }

    const returnType = typeChecker.getReturnTypeOfSignature(signature)
    const typeString = typeChecker.typeToString(returnType)

    // Parse object types to extract fields
    const fields = extractFieldsFromType(returnType, typeChecker)

    return {
      type: simplifyTypeString(typeString),
      fields: fields.length > 0 ? fields : undefined,
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    return { type: 'unknown', error: message }
  }
}

/**
 * Extracts field information from an object type
 */
function extractFieldsFromType(
  type: ts.Type,
  typeChecker: ts.TypeChecker
): Array<{ name: string; type: string; description?: string }> {
  const fields: Array<{ name: string; type: string; description?: string }> = []

  try {
    // Check if it's an object type
    if (type.isClassOrInterface() || type.symbol) {
      const properties = typeChecker.getPropertiesOfType(type)
      properties.forEach(property => {
        const propertyType = typeChecker.getTypeOfSymbolAtLocation(
          property,
          property.valueDeclaration || property.getDeclarations()?.[0] || {} as ts.Node
        )
        const typeString = typeChecker.typeToString(propertyType)

        fields.push({
          name: property.getName(),
          type: simplifyTypeString(typeString),
        })
      })
    } else if (type.getSymbol()?.members) {
      // Handle object literal types
      const members = type.getSymbol()?.members
      if (members) {
        members.forEach((member, name) => {
          const memberType = typeChecker.getTypeOfSymbolAtLocation(
            member,
            member.valueDeclaration || member.getDeclarations()?.[0] || {} as ts.Node
          )
          const typeString = typeChecker.typeToString(memberType)

          fields.push({
            name: name.toString(),
            type: simplifyTypeString(typeString),
          })
        })
      }
    }
  } catch (error) {
    // Silently fail - fields extraction is optional
    console.warn('Failed to extract fields from type:', error)
  }

  return fields
}

/**
 * Simplifies type string for better readability
 */
function simplifyTypeString(typeString: string): string {
  // Remove unnecessary prefixes
  let simplified = typeString
    .replace(/^import\(".*"\)\./, '') // Remove import paths
    .replace(/^typeof /, '') // Remove typeof prefix
    .trim()

  // Limit length for very long types
  if (simplified.length > 200) {
    simplified = simplified.substring(0, 200) + '...'
  }

  return simplified
}

/**
 * Infers the return type of a JavaScript/TypeScript code expression
 *
 * @param code - The JavaScript code to analyze (should be a return statement or expression)
 * @param variableTypes - Record of variable names and their TypeScript type strings
 * @returns Inferred type result with type string and optional fields
 *
 * @example
 * ```typescript
 * const result = inferTypeFromCode(
 *   'previousResult.latitude + previousResult.longitude',
 *   { previousResult: '{ latitude: number; longitude: number }' }
 * )
 * // result.type === 'number'
 * ```
 */
export function inferTypeFromCode(
  code: string,
  variableTypes: Record<string, string> = {}
): InferredTypeResult {
  if (!code || !code.trim()) {
    return { type: 'unknown', error: 'Code is empty' }
  }

  try {
    // Create virtual source file
    const sourceFile = createVirtualSourceFile(code, variableTypes)

    // Create a program
    const compilerOptions: ts.CompilerOptions = {
      target: ts.ScriptTarget.Latest,
      module: ts.ModuleKind.ESNext,
      allowJs: true,
      checkJs: false, // Don't check JS, just infer types
      noEmit: true,
      noLib: true, // Don't use standard library in browser
      skipLibCheck: true,
    }

    const host: ts.CompilerHost = {
      getSourceFile: (fileName) => {
        if (fileName === 'temp.ts') {
          return sourceFile
        }
        return undefined
      },
      writeFile: () => {},
      getCurrentDirectory: () => '',
      getDirectories: () => [],
      fileExists: () => true,
      readFile: () => '',
      getCanonicalFileName: (fileName) => fileName,
      useCaseSensitiveFileNames: () => true,
      getNewLine: () => '\n',
      getDefaultLibFileName: () => '', // Don't use standard library in browser
    }

    const program = ts.createProgram(['temp.ts'], compilerOptions, host)

    const typeChecker = program.getTypeChecker()

    return extractReturnType(sourceFile, typeChecker)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    return { type: 'unknown', error: `Type inference failed: ${message}` }
  }
}
